import { useEffect, useState } from "react";
import { closeModal, Modal } from "risa-oasis-ui_v2";
import { fetchMnJudgeAssessment } from "../../../api/getCalls/fetchMnJudgeAssessment";
import { LoaderMessage } from "../../loaderMessage/loaderMessage";
import { ModalId } from "../../../enums/modalId";
import AnswerAssessmentTab from "./components/answerAssessment/answerAssessmentTab";
import CriteriaTab from "./components/criteriaTab";
import HistoricalInsightsTab from "./components/historicalInsights/historicalInsightsTab";
import SummaryTab from "./components/summaryTab";
import {
  mapAssessmentToSummary,
  PredictionSummaryOrderMeta,
} from "./mapAssessmentToSummary";
import { PredictionSummaryData, PredictionSummaryTabId } from "./types";

/**
 * Props for the Prediction Summary / Evaluation Engine Output modal.
 */
interface PredictionSummaryModalProps {
  /** Callback invoked when the modal is closed. */
  onClose: () => void;
  /** PA case identifier used to fetch the MN judge assessment. */
  identifier: string;
  /** Order-level fields shown on the Summary tab. */
  orderMeta?: PredictionSummaryOrderMeta;
}

/** Tab definitions matching the Evaluation Engine Output design. */
const TABS: { id: PredictionSummaryTabId; label: string }[] = [
  { id: "summary", label: "Summary" },
  { id: "answer_assessment", label: "Answer Assessment" },
  { id: "criteria", label: "Criteria" },
  { id: "historical_insights", label: "Historical Insights" },
];

/** Shared classes for tab buttons. */
const TAB_BUTTON_CLASSES =
  "cursor-pointer border-0 border-b-2 border-solid bg-transparent px-4 py-2 text-small font-semibold transition-colors duration-200";

/**
 * Modal that fetches and displays evaluation engine output for a PA order.
 *
 * @example
 * <PredictionSummaryModal
 *   identifier={orderId}
 *   orderMeta={{ cmm_key, drug, plan, form }}
 *   onClose={() => setIsOpen(false)}
 * />
 */
const PredictionSummaryModal = (props: PredictionSummaryModalProps) => {
  const [activeTab, setActiveTab] =
    useState<PredictionSummaryTabId>("summary");
  const [data, setData] = useState<PredictionSummaryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    /**
     * Loads the latest MN judge assessment for the given identifier.
     */
    const loadAssessment = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      setData(null);

      try {
        const response = await fetchMnJudgeAssessment(props.identifier);
        if (!isMounted) {
          return;
        }

        if (!response.latest) {
          setErrorMessage("No prediction summary available for this order.");
          return;
        }

        setData(
          mapAssessmentToSummary(response.latest, {
            cmm_key: props.orderMeta?.cmm_key,
            drug: props.orderMeta?.drug,
            plan: props.orderMeta?.plan,
            form: props.orderMeta?.form,
          }),
        );
      } catch {
        if (isMounted) {
          setErrorMessage("No prediction summary available for this order.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (props.identifier) {
      loadAssessment();
    } else {
      setIsLoading(false);
      setErrorMessage("No prediction summary available for this order.");
    }

    return () => {
      isMounted = false;
    };
  }, [
    props.identifier,
    props.orderMeta?.cmm_key,
    props.orderMeta?.drug,
    props.orderMeta?.plan,
    props.orderMeta?.form,
  ]);

  /**
   * Closes the modal dialog and notifies the parent via onClose.
   */
  const handleClose = () => {
    closeModal(ModalId.PREDICTION_SUMMARY_MODAL);
    props.onClose();
  };

  /**
   * Renders the content for the currently selected tab.
   *
   * @returns Active tab panel content
   */
  const renderActiveTab = () => {
    if (!data) {
      return null;
    }

    switch (activeTab) {
      case "answer_assessment":
        return <AnswerAssessmentTab data={data} />;
      case "criteria":
        return <CriteriaTab data={data} />;
      case "historical_insights":
        return <HistoricalInsightsTab data={data} />;
      case "summary":
      default:
        return <SummaryTab data={data} />;
    }
  };

  return (
    <Modal
      dialogId={ModalId.PREDICTION_SUMMARY_MODAL}
      title="Prediction Summary"
      saveButtonText="Close"
      cancelText="Cancel"
      onSave={handleClose}
      onCancel={handleClose}
      onClose={handleClose}
      hideFooter={true}
      heightPercentage={60}
    >
      <div className="prediction-summary-modal flex h-full flex-col">
        <div className="prediction-summary-modal__tabs flex shrink-0 border-b border-primaryGray-15">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`${TAB_BUTTON_CLASSES} ${
                activeTab === tab.id
                  ? "border-tertiaryBlue-4 text-tertiaryBlue-4"
                  : "border-transparent text-primaryGray-6 hover:text-primaryGray-3"
              }`}
              onClick={() => setActiveTab(tab.id)}
              disabled={isLoading}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="prediction-summary-modal__content flex flex-1 overflow-auto pr-1 pt-2">
          {isLoading && (
            <div className="flex h-full w-full items-center justify-center">
              <LoaderMessage message="Fetching prediction summary..." />
            </div>
          )}
          {!isLoading && errorMessage && (
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-center text-small text-primaryGray-6">
                {errorMessage}
              </p>
            </div>
          )}
          {!isLoading && !errorMessage && renderActiveTab()}
        </div>
      </div>
    </Modal>
  );
};

export default PredictionSummaryModal;
